// Polyfills
import './polyfills';

// i18n
import './locales/i18n';

// highlight
import './common/highlight';

// editor
// Quill CSS removed - using Tiptap now

// lazy image
import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import 'lazysizes/plugins/object-fit/ls.object-fit';
import 'lazysizes/plugins/parent-fit/ls.parent-fit';

import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
// material
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
// redux
import * as Sentry from '@sentry/react';
import { store } from './redux/store';
import { setupSentryContext } from './common/sentryConfig';
// contexts
import { SettingsProvider } from './contexts/SettingsContext';
import { AuthProvider } from './contexts/JWTContext';
// https://github.com/atlassian/react-beautiful-dnd/issues/2350
// https://github.com/hello-pangea/dnd
import App from './App';
import reportWebVitals from './reportWebVitals';
import TransparentErrorBoundary from './components/TransparentErrorBoundary';

// Sentry
// Only initialize Sentry in production
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://1f69c92fc2ebce8cd1285639525ba9c8@o4507891895107584.ingest.us.sentry.io/4507891896811520',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    tracePropagationTargets: [/^https:\/\/worknity\.com/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  // Global error handlers for unhandled errors
  window.addEventListener('error', (event) => {
    Sentry.withScope((scope) => {
      scope.setTag('errorType', 'unhandledError');
      scope.setLevel('error');
      Sentry.captureException(event.error);
    });
  });

  // Global handler for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.withScope((scope) => {
      scope.setTag('errorType', 'unhandledRejection');
      scope.setLevel('error');
      Sentry.captureException(event.reason);
    });
  });

  // Setup global context
  setupSentryContext();
}

const container = document.getElementById('root');
// In React 19, we still need to handle null case
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

root.render(
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => <TransparentErrorBoundary resetError={resetError} />}
    beforeCapture={(scope) => {
      scope.setTag('component', 'Root');
      scope.setContext('appState', {
        timestamp: new Date().toISOString()
      });
    }}
  >
    <HelmetProvider>
      <ReduxProvider store={store}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <BrowserRouter>
            <AuthProvider>
              <SettingsProvider>
                <App />
              </SettingsProvider>
            </AuthProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </ReduxProvider>
    </HelmetProvider>
  </Sentry.ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(// console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
